import { Component, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import * as moment from "moment";
import { Moment } from "moment";
import { FiaService } from "src/app/services/fia.service";
import { MediaService } from "src/app/services/media.service";
import { SettingsService } from "src/app/services/settings.service";
import { UserService } from "src/app/services/user.service";
import { LookupFiaStatus } from "src/app/shared/models/general/lookup-fia-status";
import { LookupMediaTypes } from "src/app/shared/models/general/lookup-media-types";
import { MediaInfo } from "src/app/shared/models/general/media-info";
import { Fia } from "src/app/shared/models/users/fia";
import { FiaApplication } from "src/app/shared/models/users/fia-application";
import { UserProfile } from "src/app/shared/models/users/user-profile";

@Component({
  selector: "app-fia",
  templateUrl: "./fia.component.html",
  styleUrls: ["./fia.component.scss"],
})
export class FiaComponent implements OnInit {
  @ViewChild("userFiaApplicationModal", { static: true })
  userFiaApplicationModal: any;
  //File selector
  @ViewChild("file") file;

  busy: boolean = true;
  showError: boolean = false;
  errorMsg: string =
    "Oops! Looks like something went wrong, please contact support if this issue persists";

  modalBusy: boolean = false;
  modalShowError: boolean = false;
  modalErrorMsg: string =
    "Oops! Looks like something went wrong, please contact support if this issue persists";

  userFiaApplcations: FiaApplication[] = [];
  userProfile: UserProfile = new UserProfile();
  fiaStatus: LookupFiaStatus[] = [];
  mediaTypes: LookupMediaTypes[] = [];

  //FIA Application
  newFia: Fia = new Fia();
  currentYear = moment().year();
  startDate = moment().add(1, "days");
  userNewDocuments: MediaInfo[] = [];
  isPending: boolean = false;
  pendingTooltip: string = "";

  constructor(
    public settingsService: SettingsService,
    public usersService: UserService,
    public fiaService: FiaService,
    public mediaService: MediaService
  ) {
    this.busy = true;
    this.settingsService.ValidateGlobalVars().then(() => {
      this.settingsService.GetLookupFiaStatus().then(() => {
        this.fiaStatus = this.settingsService.fiaStatus;
        this.settingsService.GetLookupMediaTypes().then(() => {
          this.mediaTypes = this.settingsService.mediaTypes;
          this.GetFiaApplications();
          this.userProfile = this.settingsService.userProfile;
        });
      });
    });
  }

  ngOnInit(): void {}

  GetFiaApplications() {
    this.busy = true;
    this.usersService.GetUserDetails(this.settingsService.user.id).subscribe(
      (response) => {
        this.userFiaApplcations = response.fiaApplications;
        this.busy = false;
      },
      (error) => {
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
        this.busy = false;
      }
    );
  }

  modalClearErrors() {
    this.modalErrorMsg = "";
    this.modalShowError = false;
  }

  modalDisplayError(errorMessage: string) {
    if (this.modalShowError == false) {
      this.modalShowError = true;
    }
    this.modalErrorMsg = errorMessage;
  }

  modalHasErrors() {
    return this.modalShowError;
  }

  fiaApplicationApplyClicked() {
    this.modalBusy = true;

    this.modalClearErrors();

    if (!this.userNewDocuments || this.userNewDocuments.length == 0) {
      this.modalDisplayError("Please attach the supporting documents");
      this.modalBusy = false;
      return;
    }

    this.userNewDocuments.forEach((file) => {
      if (file.fileType === undefined) {
        this.modalDisplayError("You must select File Types for all Documents");
        this.modalBusy = false;
      }
    });

    if (this.modalHasErrors()) {
      return;
    }

    this.fiaService.FiaApplcation(this.newFia).subscribe(
      (response) => {
        var fiaApplication = response;
        this.UploadFiaSupportingMedia(fiaApplication).then(
          () => {
            this.GetFiaApplications();
            this.HideModal();
            this.modalClearErrors();
            this.modalBusy = false;
          },
          (error) => {
            this.modalDisplayError("Upload failed: " + error);
            this.modalBusy = false;
          }
        );
      },
      (error) => {
        this.modalDisplayError(error.error.ErrorDetail);
        this.modalBusy = false;
      }
    );
  }

  public UploadFiaSupportingMedia(fiaApplication: FiaApplication) {
    if (!this.userNewDocuments || this.userNewDocuments.length == 0) {
      return new Promise((resolve) => {
        resolve(true);
      });
    }

    return Promise.all(
      this.userNewDocuments.map(async (v) =>
        this.mediaService.UploadFiaSupportingMediaAsync(
          this.settingsService.userProfile.userId,
          fiaApplication.id,
          v
        )
      )
    ).then(
      () => {},
      (rejected) => {
        throw new Error(rejected);
      }
    );
  }

  //Moment date Functions for fia appliations
  myDateFilter = (m: Moment | null): boolean => {
    const today = m || moment();
    return today >= moment().utcOffset("+02:00").subtract(0, "days");
  };

  addFiles() {
    this.file.nativeElement.click();
  }

  onFileChange(fileInput: any) {
    var totalSize: number = 0;
    var maxSize: number = 5000000;
    var isOverMaxSize: boolean = false;

    var maxFileNameLength: number = 35;
    var longFileNameCount: number = 0;
    var longFileNames: string = "";

    var alreadyAddedCount: number = 0;
    var alreadyAddedFilenames: string = "";

    const files: { [key: string]: File } = this.file.nativeElement.files;
    for (let key in files) {
      if (!isNaN(parseInt(key))) {
        var reader = new FileReader();
        reader.onload = (event: any) => {
          if (totalSize <= maxSize && files[key].size <= maxSize) {
            if (files[key].name.length > maxFileNameLength) {
              longFileNameCount++;
              if (longFileNameCount > 1) {
                longFileNames += ", ";
              }
              longFileNames += files[key].name;
            } else {
              var isAlreadyAdded: boolean = false;
              for (let alreadyAddedKey in this.userNewDocuments) {
                if (
                  files[key].name == this.userNewDocuments[alreadyAddedKey].name
                ) {
                  isAlreadyAdded = true;
                  alreadyAddedCount++;
                  if (alreadyAddedCount > 1) {
                    alreadyAddedFilenames += ", ";
                  }
                  alreadyAddedFilenames += files[key].name;

                  break;
                }
              }

              if (isAlreadyAdded == false) {
                var file: MediaInfo = new MediaInfo();
                console.log(files[key].name);
                file.name = files[key].name;
                file.fileName = files[key].name;
                file.mimeType = files[key].type;
                file.tag = "FiaApplication";
                file.data = event.target.result;
                file.data = file.data.replace(
                  "data:" + files[key].type + ";base64,",
                  ""
                );
                file.entityId = this.userProfile.userId;
                file.entityType = this.settingsService.userEntity.description; // Users
                totalSize += files[key].size;
                this.userNewDocuments.push(file);
              }
            }
          } else {
            isOverMaxSize = true;
          }

          this.modalClearErrors();

          if (isOverMaxSize) {
            this.modalDisplayError("Max file upload size reached");
          } else {
            var errorMessage: string = "";
            if (longFileNameCount > 0) {
              errorMessage +=
                "Maximun filename length is 35 characters, please rename ";
            }
            if (longFileNameCount > 1) {
              errorMessage += longFileNameCount + " files. ";
            } else if (longFileNameCount > 0) {
              errorMessage += longFileNames + ". ";
            }
            if (alreadyAddedCount > 0) {
              errorMessage +=
                "It seems you already added " + alreadyAddedFilenames;
            }

            if (errorMessage.length > 0) {
              this.modalDisplayError(errorMessage);
            }
          }
        };

        reader.readAsDataURL(files[key]);
      }
    }
  }

  removeFile(fileName: string) {
    this.busy = true;
    this.modalBusy = true;
    this.userNewDocuments.forEach((value, index) => {
      if (value.fileName == fileName) {
        this.userNewDocuments.splice(index, 1);
      }
    });
    this.modalBusy = false;
    this.busy = false;
  }

  startOver() {
    this.busy = true;
    this.modalBusy = true;

    this.modalClearErrors();

    this.userNewDocuments = [];
    this.file.nativeElement.value = "";

    this.modalBusy = false;
    this.busy = false;
  }

  ShowModal(Modal: string) {
    if (Modal == "userFiaApplicationModal") {
      this.newFia.fiaAmount =
        this.userFiaApplcations.length > 0
          ? this.userFiaApplcations[0].requestedFiaAmount
          : 0;
      this.newFia.userId = this.settingsService.userProfile.userId;
      this.userFiaApplicationModal.show();
    }
  }

  HideModal() {
    this.modalClearErrors();
    this.userFiaApplicationModal.hide();
  }
} 
